// Google Calendar on a Calendar board, as the browser, the server and the agent's tools all read it: a connected
// Google account and its calendars, an event as the board keeps it (read from Google's own shape, on its own zone's
// clock as the board's own events are), the colors Google gives calendars and events, and which calendars a board
// shows. The server's side (sign-in, reading, push channels) is worker/gcal.ts.

import { addDays, cleanDay, cleanZone } from "./days";
import { BOARD_CAL, calSetOf, localAt, momentOf, type CalSetData } from "./agenda";
import { joinFromGoogle, REPLIES, type Join, type Reply } from "./meet";
import type { Entry, ID } from "./types";

/** What the board asks Google for, once, for good: to see and edit events, and to see the list of calendars. */
export const GCAL_EVENTS_SCOPE = "https://www.googleapis.com/auth/calendar.events";
export const GCAL_LIST_SCOPE = "https://www.googleapis.com/auth/calendar.calendarlist.readonly";
export const GCAL_SCOPES = [GCAL_EVENTS_SCOPE, GCAL_LIST_SCOPE, "openid", "https://www.googleapis.com/auth/userinfo.email"];

export type GcalRole = "owner" | "writer" | "reader" | "freeBusyReader";

export interface GcalCalendar {
  /** Google's id for it ("david@contextbolt.com", "…@group.calendar.google.com") */
  id: string;
  /** the board's key for it, "g:<account>:<calendar>", as a Calendar board's settings name it */
  key: string;
  name: string;
  /** its color, "#rrggbb", as Google gives it */
  color: string;
  role: GcalRole;
  primary: boolean;
  /** shown in Google Calendar's own list; a board shows these until told otherwise */
  selected: boolean;
  tz: string | null;
  /** when its events were last read whole or brought up to date; null before the first read */
  synced: number | null;
  /** why the last read failed, in words for the user; null when it worked */
  error: string | null;
}

export interface GcalAccount {
  id: string;
  email: string;
  /** why the account stopped working (its access was taken back), in words; null when it works */
  error: string | null;
  calendars: GcalCalendar[];
  /** calendars taken off the board (Fixes 9; the calendars column no longer does this, Fixes 11), which Connect can add back */
  removed?: { id: string; name: string }[];
}

export interface GcalStatus {
  /** the board can sign in to Google here at all */
  available: boolean;
  accounts: GcalAccount[];
}

/** An event from a Google calendar, as the board keeps and shows it: the board's own shape, and where it came from. */
export interface GcalEvent {
  /** the calendar's key ("g:<account>:<calendar>") */
  cal: string;
  /** Google's id for the event (for a repeat, this occurrence's) */
  id: string;
  title: string;
  allDay: boolean;
  /** as the board's own events: a timed event's times on its zone's clock, an all-day event's first and last day */
  start: string;
  end: string;
  tz: string | null;
  location?: string;
  notes?: string;
  /** its own color, "#rrggbb", when it has one in Google; else its calendar's */
  color?: string;
  /** Google Calendar's page for it */
  link?: string;
  /** "tentative" when Google says so */
  status?: "tentative";
  /** an occurrence of a repeating event */
  recurring?: boolean;
  /** the repeating event it is one of (Google's recurringEventId), and the day it was due to start on */
  series?: string;
  occ?: string;
  /** Google's version of it, for a change sent back (step 3) */
  etag?: string;
  /** its video call (step 5): Google's meeting details, else a meeting link in its place or notes */
  join?: Join;
  /** how many guests it has besides you; who they are is read from Google when its card opens, never kept */
  guests?: number;
  /** your own answer, when you were invited */
  you?: Reply;
  /** who invited you, when it is someone else's event (its organizer's name, else address): only they change it */
  from?: string;
}

/** A calendar's key on the board. */
export const calKey = (account: ID, calendar: string) => `g:${account}:${calendar}`;

/** Google's event colors, by colorId (Calendar's colors endpoint gives these, fixed since 2012). */
export const EVENT_COLORS: Record<string, string> = { "1": "#7986cb", "2": "#33b679", "3": "#8e24aa", "4": "#e67c73", "5": "#f6bf26", "6": "#f4511e", "7": "#039be5", "8": "#616161", "9": "#3f51b5", "10": "#0b8043", "11": "#d50000" };

/** A color as "#rrggbb", or null. */
export function hexColor(x: unknown): string | null {
  return typeof x === "string" && /^#[0-9a-f]{6}$/i.test(x.trim()) ? x.trim().toLowerCase() : null;
}

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : {});
const str = (v: unknown, max: number): string => (typeof v === "string" ? v.slice(0, max) : "");

/** A description's words, from the little HTML Google Calendar keeps in it: lines kept, links as their addresses, tags gone. */
export function plainNotes(html: string): string {
  const withLinks = html.replace(/<a\b[^>]*href\s*=\s*"([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_m, href: string, words: string) => {
    const w = words.replace(/<[^>]*>/g, "").trim();
    return w && w !== href ? `${w} (${href})` : href;
  });
  const text = withLinks
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h\d)>/gi, "\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
  return text.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * An event from Google's API as the board keeps it, or null for one that is cancelled or cannot be read. A timed
 * event keeps its times on the zone Google names for it (else the calendar's), as the board's own events do; an all-day
 * event's end is Google's day after it, so its last day is the one before.
 */
export function fromGoogle(item: unknown, cal: string, calTz: string | null): GcalEvent | null {
  const e = obj(item);
  if (e.status === "cancelled" || typeof e.id !== "string" || !e.id) return null;
  const s = obj(e.start);
  const f = obj(e.end);
  let allDay: boolean;
  let start: string;
  let end: string;
  let tz: string | null = null;
  if (typeof s.date === "string") {
    const a = cleanDay(s.date);
    if (!a) return null;
    const after = typeof f.date === "string" ? cleanDay(f.date) : null;
    const last = after ? addDays(after, -1) : a;
    allDay = true;
    start = a;
    end = last < a ? a : last;
  } else if (typeof s.dateTime === "string") {
    const t0 = Date.parse(s.dateTime);
    const t1 = typeof f.dateTime === "string" ? Date.parse(f.dateTime) : NaN;
    if (!Number.isFinite(t0)) return null;
    tz = cleanZone(s.timeZone) ?? cleanZone(calTz) ?? "UTC";
    allDay = false;
    start = localAt(t0, tz);
    end = localAt(Number.isFinite(t1) && t1 > t0 ? t1 : t0 + 30 * 60_000, tz);
    // a zero-length event still shows: half an hour, as Google draws it
    if (end <= start) end = localAt(t0 + 30 * 60_000, tz);
  } else return null;
  const title = str(e.summary, 300).replace(/[\r\n\t]+/g, " ").trim();
  const location = str(e.location, 300).replace(/[\r\n\t]+/g, " ").trim();
  const notes = typeof e.description === "string" ? plainNotes(e.description.slice(0, 20_000)).slice(0, 8000) : "";
  const color = typeof e.colorId === "string" ? EVENT_COLORS[e.colorId] : undefined;
  const link = typeof e.htmlLink === "string" && /^https:\/\/(www\.)?google\.com\/calendar\//.test(e.htmlLink) ? e.htmlLink.slice(0, 500) : undefined;
  // meetings (step 5): the call's link, how many guests (never who), your own answer, and who invited you
  const join = joinFromGoogle(e, location, notes);
  const people = Array.isArray(e.attendees) ? (e.attendees as unknown[]).map(obj) : [];
  // your own entry as a guest: on a meeting you organize Google lists you too, with nothing to answer
  const self = people.find((p) => p.self === true && p.organizer !== true);
  const guests = people.filter((p) => p.self !== true && p.resource !== true).length;
  const you = self && (REPLIES as unknown[]).includes(self.responseStatus) ? (self.responseStatus as Reply) : undefined;
  const org = obj(e.organizer);
  const from = Object.keys(org).length && org.self !== true ? str(org.displayName, 120).trim() || str(org.email, 200).trim() : "";
  return {
    cal,
    id: e.id.slice(0, 1024),
    title,
    allDay,
    start,
    end,
    tz,
    ...(location ? { location } : {}),
    ...(notes ? { notes } : {}),
    ...(color ? { color } : {}),
    ...(link ? { link } : {}),
    ...(e.status === "tentative" ? { status: "tentative" as const } : {}),
    ...(typeof e.recurringEventId === "string" ? { recurring: true, series: e.recurringEventId.slice(0, 1024), ...(occOf(e.originalStartTime, tz) ? { occ: occOf(e.originalStartTime, tz)! } : {}) } : {}),
    ...(typeof e.etag === "string" ? { etag: e.etag.slice(0, 200) } : {}),
    ...(join ? { join } : {}),
    ...(guests ? { guests } : {}),
    ...(you ? { you } : {}),
    ...(from ? { from } : {}),
  };
}

/** The day a repeating event's occurrence was due to start, on its own clock, from Google's originalStartTime. */
function occOf(x: unknown, tz: string | null): string | null {
  const o = obj(x);
  if (typeof o.date === "string") return cleanDay(o.date);
  if (typeof o.dateTime === "string" && Number.isFinite(Date.parse(o.dateTime))) return localAt(Date.parse(o.dateTime), cleanZone(o.timeZone) ?? tz).slice(0, 10);
  return null;
}

/** The board's named colors as Google's event colors (colorId), and back. */
export const COLOR_ID: Record<string, string> = { red: "11", orange: "6", yellow: "5", green: "2", blue: "7", purple: "3", grey: "8" };

/** The moments an event covers, for finding the events in a span of days whatever the viewer's zone: a day each side for all-day ones. */
export function spanOf(ev: Pick<GcalEvent, "allDay" | "start" | "end" | "tz">): { t0: number; t1: number } {
  if (ev.allDay) {
    const a = Date.UTC(+ev.start.slice(0, 4), +ev.start.slice(5, 7) - 1, +ev.start.slice(8, 10));
    const b = Date.UTC(+ev.end.slice(0, 4), +ev.end.slice(5, 7) - 1, +ev.end.slice(8, 10)) + 86_400_000;
    return { t0: a - 14 * 3_600_000, t1: b + 14 * 3_600_000 };
  }
  return { t0: momentOf(ev.start, ev.tz), t1: momentOf(ev.end, ev.tz) };
}

/**
 * Whether a board shows a calendar: one in the board's list that is ticked. The list is the calendars Google shows in
 * its own list, less those taken out on this board, and any other Google calendar the board added; a ticked one is
 * not hidden. The board's own is shown while it is not hidden.
 */
export function boardShows(set: CalSetData, key: string, selectedInGoogle: boolean): boolean {
  return inBoardList(set, key, selectedInGoogle) && !set.hidden.includes(key);
}

/** Whether a calendar is in a board's list (ticked or not): Google's own list less what the board took out, plus what it added. */
export function inBoardList(set: CalSetData, key: string, selectedInGoogle: boolean): boolean {
  if (key === BOARD_CAL) return true;
  return selectedInGoogle ? !(set.out ?? []).includes(key) : (set.shown ?? []).includes(key);
}

/** The theme's own colours a calendar can take, by the name kept; each is drawn by the theme in view (src/themes/themes.ts --tc-*, the sheet's palette). */
export const CAL_THEME_COLORS = [
  { id: "red", name: "Red", css: "var(--tc-red)" },
  { id: "orange", name: "Orange", css: "var(--tc-orange)" },
  { id: "yellow", name: "Yellow", css: "var(--tc-yellow)" },
  { id: "green", name: "Green", css: "var(--tc-green)" },
  { id: "blue", name: "Blue", css: "var(--tc-blue)" },
  { id: "purple", name: "Purple", css: "var(--tc-purple)" },
  { id: "grey", name: "Gray", css: "var(--tc-grey)" },
  { id: "accent", name: "Accent", css: "var(--accent)" },
] as const;

/** The colours calendars chose, by calendar key: a theme colour's name, or "#rrggbb" of its own. */
export type CalColors = Record<string, string>;

export function cleanCalColors(raw: unknown): CalColors | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out: CalColors = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>).slice(0, 200)) {
    const pick = typeof v === "string" ? (CAL_THEME_COLORS.some((c) => c.id === v) ? v : hexColor(v)) : null;
    if (pick && /^[^\s\u0000-\u001f\u007f]{1,400}$/.test(k)) out[k] = pick;
  }
  return Object.keys(out).length ? out : null;
}

/** A calendar's colour as CSS: the one it chose (a theme colour follows the theme; a hex stays as chosen), else Google's. */
export function calCss(pick: string | undefined, google: string): string {
  return CAL_THEME_COLORS.find((c) => c.id === pick)?.css ?? hexColor(pick) ?? google;
}

/** A holiday calendar, which Google makes for each country and language ("en.uk#holiday@group.v.calendar.google.com"). */
const HOLIDAY_CAL = /#holiday@group\.v\.calendar\.google\.com$/;

/**
 * The same holiday from several holiday calendars once (Fixes 11: Halloween showed three times from the UK calendars of
 * two accounts): of events on holiday calendars, one with the same title on the same days; every other event stays.
 * `idOf` gives a calendar's Google id from its key.
 */
export function oneOfEachHoliday<T extends { cal: string; title: string; start: string; end: string; allDay: boolean }>(list: T[], idOf: (cal: string) => string | undefined): T[] {
  const seen = new Set<string>();
  return list.filter((e) => {
    if (!e.allDay || !HOLIDAY_CAL.test(idOf(e.cal) ?? "")) return true;
    const k = `${e.title.trim().toLowerCase()}|${e.start}|${e.end}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * A calendar two connected accounts both have (a country's holidays, a calendar shared with both): every copy but one,
 * by key, so a board lists it and shows its events once (Fixes 10: UK holidays showed three times with two accounts).
 * The copy kept is a working account's, and of those the one the board shows (shows), else the first (review: a broken
 * account's copy hid the working one).
 */
export function copiesOf(accounts: { error?: string | null; calendars: { id: string; key: string; selected?: boolean }[] }[] | undefined, shows?: (key: string, selected: boolean) => boolean): Set<string> {
  const groups = new Map<string, { key: string; rank: number }[]>();
  for (const a of accounts ?? [])
    for (const c of a.calendars) groups.set(c.id, [...(groups.get(c.id) ?? []), { key: c.key, rank: (a.error ? 2 : 0) + (shows && !shows(c.key, !!c.selected) ? 1 : 0) }]);
  const out = new Set<string>();
  for (const list of groups.values()) {
    if (list.length < 2) continue;
    const keep = list.reduce((best, x) => (x.rank < best.rank ? x : best));
    for (const x of list) if (x !== keep) out.add(x.key);
  }
  return out;
}

/** Every Google calendar any Calendar board shows: the ones the board keeps up to date. */
export function calendarsShown(boards: { id: ID }[], entries: Entry[], calendars: { key: string; selected: boolean }[]): Set<string> {
  const out = new Set<string>();
  for (const b of boards) {
    const set = calSetOf(entries, b.id);
    for (const c of calendars) if (boardShows(set, c.key, c.selected)) out.add(c.key);
  }
  return out;
}

/** The Google events that are a board event's copies (Fixes 11), as "<calendar key>#<Google's id>": the board shows such an event once, from its own entry. */
export function linkedCopies(entries: Entry[]): Set<string> {
  const out = new Set<string>();
  for (const e of entries) if (e.t === "event") for (const [cal, id] of Object.entries((e.data as unknown as { g?: Record<string, string> }).g ?? {})) out.add(`${cal}#${id}`);
  return out;
}

/** Whether a Google event read from a calendar is one of those copies (or a time of a repeating copy). */
export const isLinkedCopy = (linked: Set<string>, g: { cal: string; id: string; series?: string }): boolean => linked.has(`${g.cal}#${g.id}`) || (!!g.series && linked.has(`${g.cal}#${g.series}`));

/**
 * Whether a board event shows on a board: an event with copies in Google calendars shows while the board shows one of
 * them; one with none (or whose calendars are gone) is only on the board, and shows while that is not hidden.
 * `known` says whether a calendar is still connected, and if so whether Google lists it as selected.
 */
export function boardEventShows(set: CalSetData, copies: string[] | undefined, known: (key: string) => { selected: boolean } | null): boolean {
  const live = (copies ?? []).map((k) => ({ k, c: known(k) })).filter((x) => x.c);
  if (!live.length) return !set.hidden.includes(BOARD_CAL);
  return live.some((x) => boardShows(set, x.k, x.c!.selected));
}
